import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import './CustomTree.scss';
import UtilityService from '../../services/UtillityServices/UtilityService';
import { alertService } from '../../services/AlertService';

const FOLDER_EXPAND_DELAY_MS = 2000;
/** Parte bassa della riga cartella = rilascio come sibling (dopo la cartella). */
const DROP_AFTER_ZONE_RATIO = 0.55;

const AREA_KEY_PREFIX = 'a-';
const CONT_KEY_PREFIX = 'c-';

const buildAreaKey = (idArea) => `${AREA_KEY_PREFIX}${idArea}`;
const buildContKey = (idContenuto) => `${CONT_KEY_PREFIX}${idContenuto}`;

const isAreaKey = (key) => typeof key === 'string' && key.startsWith(AREA_KEY_PREFIX);
const isContKey = (key) => typeof key === 'string' && key.startsWith(CONT_KEY_PREFIX);

/** Ordine condiviso tra contenuti e sotto-cartelle dello stesso parent. */
const sortMixedSiblings = (a, b) => {
    const oa = Number(a.ordinamento) || 0;
    const ob = Number(b.ordinamento) || 0;
    if (oa !== ob) return oa - ob;
    // Stesso ordinamento: contenuti prima (compatibilita' corsi esistenti), poi cartelle
    if (a.kind !== b.kind) {
        return a.kind === 'cont' ? -1 : 1;
    }
    const ida = a.kind === 'cont' ? a.data.id_contenuto : a.data.id_area;
    const idb = b.kind === 'cont' ? b.data.id_contenuto : b.data.id_area;
    return (ida || 0) - (idb || 0);
};

const buildMixedAreaChildren = (area, walk) => {
    const mixed = [
        ...(area.contenuti || []).map((c) => ({
            kind: 'cont',
            ordinamento: c.ordinamento,
            data: c,
        })),
        ...(area.children || []).map((sub) => ({
            kind: 'area',
            ordinamento: sub.ordinamento,
            data: sub,
        })),
    ].sort(sortMixedSiblings);

    return mixed.map((item) => {
        if (item.kind === 'cont') {
            const c = item.data;
            return {
                key: buildContKey(c.id_contenuto),
                title: c.titolo,
                isLeaf: true,
                nodeType: 'cont',
                raw: c,
            };
        }
        return walk(item.data);
    });
};

/**
 * Costruisce il treeData nel formato richiesto da rc-tree a partire
 * dal nostro modello (aree con children + contenuti).
 *
 * Ordine di rendering: i nodi vengono restituiti già ordinati per il
 * campo `ordine` (con fallback su id) - il sort lo gestisce il backend,
 * qui ci limitiamo a mantenere l'ordine ricevuto.
 *
 * Ogni nodo include i metadati grezzi (`raw`) per poterli reinviare al
 * backend in fase di drop.
 */
const convertAreasToRcTreeData = (areas) => {
    const walk = (area) => {
        const areaKey = buildAreaKey(area.id_area);
        const children = buildMixedAreaChildren(area, walk);

        return {
            key: areaKey,
            title: area.titolo,
            nodeType: 'area',
            raw: area,
            children,
        };
    };

    return (areas || []).map(walk);
};

/**
 * Verifica se `candidateKey` è un discendente di `nodeKey` nel treeData.
 * Usato per evitare cicli quando si trascina un'area dentro un suo
 * discendente.
 */
const findNodeContext = (nodes, key, parentKey = null, parentNodeType = null) => {
    for (const n of nodes || []) {
        if (n.key === key) {
            return { node: n, parentKey, parentNodeType, isRoot: parentKey === null };
        }
        if (n.children?.length) {
            const found = findNodeContext(n.children, key, n.key, n.nodeType);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Un contenuto non puo' essere sibling di un'area root: finirebbe fuori da
 * qualsiasi cartella. rc-tree non espone `pos` su dropNode in allowDrop, quindi
 * risaliamo al parent reale nel treeData.
 */
const wouldContentBeOutsideArea = (treeData, dropNode, dropPosition) => {
    if (!dropNode) return true;

    if (dropPosition === 0) {
        return dropNode.nodeType !== 'area';
    }

    const ctx = findNodeContext(treeData, dropNode.key);
    if (!ctx || ctx.isRoot) {
        return true;
    }

    return ctx.parentNodeType !== 'area';
};

const isDescendant = (treeData, nodeKey, candidateKey) => {
    const findNode = (nodes, key) => {
        for (const n of nodes) {
            if (n.key === key) return n;
            if (n.children?.length) {
                const found = findNode(n.children, key);
                if (found) return found;
            }
        }
        return null;
    };
    const containsKey = (nodes, key) => {
        for (const n of nodes) {
            if (n.key === key) return true;
            if (n.children?.length && containsKey(n.children, key)) return true;
        }
        return false;
    };
    const node = findNode(treeData, nodeKey);
    if (!node || !node.children?.length) return false;
    return containsKey(node.children, candidateKey);
};

/**
 * Clona ricorsivamente il treeData escludendo i campi non serializzabili
 * (in particolare `title`, che e' un React element con riferimenti
 * ciclici interni). Manteniamo solo cio' che serve a `flattenForBackend`.
 */
const cloneTreeForBackend = (nodes) =>
    (nodes || []).map((n) => ({
        key: n.key,
        nodeType: n.nodeType,
        raw: n.raw,
        isLeaf: n.isLeaf,
        title: typeof n.title === 'string' ? n.title : n.raw?.titolo,
        children: n.children ? cloneTreeForBackend(n.children) : undefined,
    }));

const getRelativeDropPosition = (info) => {
    const pos = info?.node?.pos;
    if (!pos) {
        return info?.dropToGap ? 1 : 0;
    }
    const dropPos = pos.split('-');
    return info.dropPosition - Number(dropPos[dropPos.length - 1]);
};

/**
 * Applica al treeData lo spostamento risultato del drag&drop.
 * Implementazione ispirata all'esempio standard di antd/rc-tree, ma
 * con clone manuale per evitare cicli da React element (title).
 */
const applyDrop = (treeData, info) => {
    const dropKey = info.node.key;
    const dragKey = info.dragNode.key;
    const dropPosition = getRelativeDropPosition(info);

    const data = cloneTreeForBackend(treeData);

    const loop = (nodes, key, callback) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].key === key) {
                return callback(nodes[i], i, nodes);
            }
            if (nodes[i].children) {
                loop(nodes[i].children, key, callback);
            }
        }
    };

    let dragObj;
    loop(data, dragKey, (item, index, arr) => {
        arr.splice(index, 1);
        dragObj = item;
    });

    if (!info.dropToGap) {
        loop(data, dropKey, (item) => {
            item.children = item.children || [];
            if (info.dropAppend) {
                item.children.push(dragObj);
            } else {
                item.children.unshift(dragObj);
            }
        });
    } else {
        let ar = [];
        let i = 0;
        loop(data, dropKey, (_item, index, arr) => {
            ar = arr;
            i = index;
        });
        if (dropPosition === -1) {
            ar.splice(i, 0, dragObj);
        } else {
            ar.splice(i + 1, 0, dragObj);
        }
    }
    return data;
};

/** Inserisce dragKey subito dopo afterKey (stesso parent), senza dipendere da dropPosition rc-tree. */
const moveNodeAfterSibling = (treeData, dragKey, afterKey) => {
    if (!dragKey || !afterKey || dragKey === afterKey) {
        return cloneTreeForBackend(treeData);
    }

    const data = cloneTreeForBackend(treeData);
    let dragObj;

    const loop = (nodes, key, callback) => {
        for (let i = 0; i < nodes.length; i++) {
            if (nodes[i].key === key) {
                return callback(nodes[i], i, nodes);
            }
            if (nodes[i].children) {
                loop(nodes[i].children, key, callback);
            }
        }
    };

    loop(data, dragKey, (_item, index, arr) => {
        arr.splice(index, 1);
        dragObj = _item;
    });

    if (!dragObj) {
        return data;
    }

    let inserted = false;
    loop(data, afterKey, (_item, index, arr) => {
        arr.splice(index + 1, 0, dragObj);
        inserted = true;
    });

    return inserted ? data : treeData;
};

const resolveDropAfterFolderKey = (info, afterKeys, treeData) => {
    if (!afterKeys?.length || info?.dragNode?.nodeType !== 'cont') {
        return null;
    }

    const dropKey = info?.node?.key;
    if (dropKey && afterKeys.includes(dropKey) && info?.node?.nodeType === 'area') {
        return dropKey;
    }

    for (let i = afterKeys.length - 1; i >= 0; i -= 1) {
        const key = afterKeys[i];
        if (isAreaKey(key) && findNodeContext(treeData, key)) {
            return key;
        }
    }

    return null;
};

/** Allinea raw.contenuti / raw.children e ordinamento al tree visuale post-drop. */
const syncAreaNodeRaw = (areaNode, parentAreaId, siblingIdx) => {
    areaNode.raw = {
        ...areaNode.raw,
        ordinamento: siblingIdx,
        id_area_padre: parentAreaId,
    };

    const contenuti = [];
    const children = [];

    (areaNode.children || []).forEach((child, childIdx) => {
        if (child.nodeType === 'cont') {
            child.raw = {
                ...child.raw,
                ordinamento: childIdx,
                id_area: areaNode.raw.id_area,
            };
            contenuti.push(child.raw);
        } else if (child.nodeType === 'area') {
            syncAreaNodeRaw(child, areaNode.raw.id_area, childIdx);
            children.push(child.raw);
        }
    });

    areaNode.raw = { ...areaNode.raw, contenuti, children };
};

const syncRawOrdinamentoFromTree = (nodes, parentAreaId = 0) => {
    (nodes || []).forEach((node, idx) => {
        if (node.nodeType === 'area') {
            syncAreaNodeRaw(node, parentAreaId, idx);
        }
    });
};

/**
 * Visita il treeData ricostruito e produce gli array piatti da inviare
 * al backend: ogni nodo riporta il nuovo parent e il nuovo `ordinamento`
 * (indice fra i suoi siblings, 0-based).
 *
 * Il nome del campo e' `ordinamento` per allinearsi alla colonna gia'
 * presente in produzione su `contenuti` (letta dall'area corsista).
 */
const flattenForBackend = (treeData) => {
    const aree = [];
    const contenuti = [];

    const walk = (nodes, parentAreaId) => {
        nodes.forEach((node, idx) => {
            if (node.nodeType === 'area') {
                aree.push({
                    id_area: node.raw.id_area,
                    id_area_padre: parentAreaId,
                    ordinamento: idx,
                });
                if (node.children?.length) {
                    walk(node.children, node.raw.id_area);
                }
            } else if (node.nodeType === 'cont') {
                contenuti.push({
                    id_contenuto: node.raw.id_contenuto,
                    id_area: parentAreaId,
                    ordinamento: idx,
                });
            }
        });
    };

    walk(treeData, 0);
    return { aree, contenuti };
};

/**
 * Se il contenuto viene rilasciato sulla riga/gap di una cartella root,
 * interpreta lo spostamento come ingresso in un'altra area (non a livello root).
 */
const getDropIntentFromEvent = (event) => {
    const row = event?.target?.closest?.('.rc-tree-treenode');
    if (!row) return 'inside';
    const rect = row.getBoundingClientRect();
    if (!rect.height) return 'inside';
    const ratio = (event.clientY - rect.top) / rect.height;
    return ratio >= DROP_AFTER_ZONE_RATIO ? 'after' : 'inside';
};

const normalizeDropInfo = (treeData, info) => {
    const dragNode = info?.dragNode;
    const dropNode = info?.node;
    if (dragNode?.nodeType !== 'cont' || !dropNode || !info.dropToGap) {
        return info;
    }

    const relativePosition = getRelativeDropPosition(info);
    const ctx = findNodeContext(treeData, dropNode.key);

    if (dropNode.nodeType !== 'area' || !ctx?.isRoot) {
        return info;
    }

    if (relativePosition <= 0) {
        return { ...info, dropToGap: false, dropAppend: false };
    }

    const idx = treeData.findIndex((n) => n.key === dropNode.key);
    const nextRoot = treeData[idx + 1];
    if (nextRoot?.nodeType === 'area') {
        return {
            ...info,
            node: nextRoot,
            dropToGap: false,
            dropAppend: false,
        };
    }

    return { ...info, dropToGap: false, dropAppend: true };
};

const CustomTree = ({
    areas,
    setSelectedNodeTree,
    setModalStateAree,
    setInputAreaValue,
    setSelectedContenuto,
    idCorso,
    onReorder,
}) => {
    const [expandedKeys, setExpandedKeys] = useState(() => {
        const saved = localStorage.getItem('openFolders');
        if (!saved) return [];
        try {
            const parsed = JSON.parse(saved);
            return Array.isArray(parsed) ? parsed.map((id) => buildAreaKey(id)) : [];
        } catch {
            return [];
        }
    });

    const [selectedKeys, setSelectedKeys] = useState(() => {
        const cid = localStorage.getItem('selectedContenutoId');
        return cid ? [buildContKey(parseInt(cid, 10))] : [];
    });

    useEffect(() => {
        const ids = expandedKeys
            .filter(isAreaKey)
            .map((k) => parseInt(k.replace(AREA_KEY_PREFIX, ''), 10));
        localStorage.setItem('openFolders', JSON.stringify(ids));
    }, [expandedKeys]);

    const expandTimerRef = useRef(null);
    const pendingExpandKeyRef = useRef(null);
    const [expandPendingKey, setExpandPendingKey] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragCollapsedKeys, setDragCollapsedKeys] = useState([]);
    const [dropAfterKeys, setDropAfterKeys] = useState([]);
    const [displayTreeData, setDisplayTreeData] = useState(null);

    const clearExpandTimer = useCallback(() => {
        if (expandTimerRef.current) {
            clearTimeout(expandTimerRef.current);
            expandTimerRef.current = null;
        }
        pendingExpandKeyRef.current = null;
        setExpandPendingKey(null);
    }, []);

    useEffect(() => () => clearExpandTimer(), [clearExpandTimer]);

    const clearDragCollapseState = useCallback(() => {
        setDragCollapsedKeys([]);
        setDropAfterKeys([]);
    }, []);

    const markDropAfterFolder = useCallback((areaKey) => {
        clearExpandTimer();
        setDragCollapsedKeys((prev) => (prev.includes(areaKey) ? prev : [...prev, areaKey]));
        setDropAfterKeys((prev) => (prev.includes(areaKey) ? prev : [...prev, areaKey]));
    }, [clearExpandTimer]);

    const markDropInsideFolder = useCallback((areaKey) => {
        setDragCollapsedKeys((prev) => prev.filter((k) => k !== areaKey));
        setDropAfterKeys((prev) => prev.filter((k) => k !== areaKey));
    }, []);

    const effectiveExpandedKeys = useMemo(() => {
        if (!isDragging || dragCollapsedKeys.length === 0) {
            return expandedKeys;
        }
        return expandedKeys.filter((k) => !dragCollapsedKeys.includes(k));
    }, [expandedKeys, isDragging, dragCollapsedKeys]);

    const scheduleFolderExpand = useCallback((areaKey) => {
        if (!areaKey || !isAreaKey(areaKey)) return;
        if (expandedKeys.includes(areaKey)) return;
        if (pendingExpandKeyRef.current === areaKey && expandTimerRef.current) {
            return;
        }

        clearExpandTimer();
        pendingExpandKeyRef.current = areaKey;
        setExpandPendingKey(areaKey);

        expandTimerRef.current = setTimeout(() => {
            setExpandedKeys((prev) => (prev.includes(areaKey) ? prev : [...prev, areaKey]));
            clearExpandTimer();
        }, FOLDER_EXPAND_DELAY_MS);
    }, [clearExpandTimer, expandedKeys]);

    const openModalEditArea = useCallback((area) => {
        setSelectedNodeTree(area);
        setInputAreaValue(area.titolo);
        setModalStateAree((state) => ({
            ...state,
            showModalUpdateAreaOrSottoarea: true,
        }));
    }, [setSelectedNodeTree, setInputAreaValue, setModalStateAree]);

    const renderTreeTitle = useCallback((nodeData) => {
        const type = nodeData?.nodeType;
        const data = nodeData?.raw;
        if (!data) return nodeData?.title;

        if (type === 'area') {
            const hasChildren =
                (data.children && data.children.length > 0) ||
                (data.contenuti && data.contenuti.length > 0);
            const areaKey = buildAreaKey(data.id_area);
            const isExpandPending = expandPendingKey === areaKey;
            const isDropAfter = dropAfterKeys.includes(areaKey);

            return (
                <span
                    className={`custom-tree-node-title custom-tree-area${
                        isExpandPending ? ' custom-tree-area--expand-pending' : ''
                    }${isDropAfter ? ' custom-tree-area--drop-after' : ''}`}
                    title={data.titolo}
                >
                    <span
                        className={`bi ${hasChildren ? 'bi-folder-fill' : 'bi-folder'} text-primary me-1`}
                    />
                    <span className="custom-tree-label">{data.titolo}</span>
                    {isExpandPending && (
                        <span className="custom-tree-expand-pending" aria-live="polite">
                            <i className="bi bi-hourglass-split" aria-hidden="true" />
                            <span>Apertura...</span>
                            <span className="custom-tree-expand-pending-bar" />
                        </span>
                    )}
                    {isDropAfter && !isExpandPending && (
                        <span className="custom-tree-drop-after-hint" aria-live="polite">
                            <i className="bi bi-arrow-down-short" aria-hidden="true" />
                            <span>Dopo cartella</span>
                        </span>
                    )}
                    <span
                        role="button"
                        tabIndex={-1}
                        className="bi bi-pencil edit-icon text-dark ms-1 small"
                        onClick={(e) => {
                            e.stopPropagation();
                            openModalEditArea(data);
                        }}
                    />
                </span>
            );
        }

        return (
            <span className="custom-tree-node-title custom-tree-cont" title={data.titolo}>
                <span className="me-1">{UtilityService.generateIconContenuti(data)}</span>
                <span className="custom-tree-label">{data.titolo}</span>
            </span>
        );
    }, [openModalEditArea, expandPendingKey, dropAfterKeys]);

    const baseTreeData = useMemo(
        () => convertAreasToRcTreeData(areas),
        [areas]
    );

    useEffect(() => {
        setDisplayTreeData(null);
    }, [baseTreeData]);

    const treeData = displayTreeData ?? baseTreeData;

    const onSelect = (keys, info) => {
        const node = info?.node;
        if (!node) return;
        if (node.nodeType === 'cont') {
            setSelectedContenuto(node.raw);
            try {
                localStorage.setItem('selectedContenutoId', node.raw.id_contenuto);
            } catch {}
            setSelectedKeys([node.key]);
        } else if (node.nodeType === 'area') {
            setExpandedKeys((prev) => {
                if (prev.includes(node.key)) {
                    return prev.filter((k) => k !== node.key);
                }
                return [...prev, node.key];
            });
            setSelectedKeys([]);
        }
    };

    const onExpand = (keys) => {
        setExpandedKeys(keys);
    };

    /**
     * Filtra i drop "non permessi" prima ancora che vengano applicati.
     * - Un contenuto non puo' finire a livello root (gli serve un'area).
     * - Un'area non puo' essere spostata dentro se stessa o un proprio discendente.
     * - Non si puo' rilasciare "dentro" un contenuto (sono foglie).
     */
    const allowDrop = ({ dragNode, dropNode, dropPosition }) => {
        if (!dragNode || !dropNode) return false;

        if (dragNode.key === dropNode.key) {
            return false;
        }

        if (dropNode.nodeType === 'cont' && dropPosition === 0) {
            return false;
        }

        if (dragNode.nodeType === 'cont') {
            if (dropPosition === 0) {
                return dropNode.nodeType === 'area';
            }

            const ctx = findNodeContext(treeData, dropNode.key);
            if (dropNode.nodeType === 'area' && ctx?.isRoot) {
                return true;
            }

            if (wouldContentBeOutsideArea(treeData, dropNode, dropPosition)) {
                return false;
            }
        }

        if (dragNode.nodeType === 'area') {
            if (isDescendant(treeData, dragNode.key, dropNode.key)) {
                return false;
            }
        }

        return true;
    };

    const resolveAreaKeyFromEventNode = (node) => {
        if (!node) return null;
        if (isAreaKey(node.key)) return node.key;
        if (node.nodeType === 'area' && node.raw?.id_area) {
            return buildAreaKey(node.raw.id_area);
        }
        return null;
    };

    const handleFolderDragHover = useCallback((info) => {
        const areaKey = resolveAreaKeyFromEventNode(info?.node);
        if (!areaKey) return;

        const intent = getDropIntentFromEvent(info?.event);
        if (intent === 'after') {
            markDropAfterFolder(areaKey);
            return;
        }

        markDropInsideFolder(areaKey);
        if (!expandedKeys.includes(areaKey)) {
            scheduleFolderExpand(areaKey);
        }
    }, [expandedKeys, markDropAfterFolder, markDropInsideFolder, scheduleFolderExpand]);

    const onDragStart = () => {
        setIsDragging(true);
        clearDragCollapseState();
    };

    const onDragOver = (info) => {
        handleFolderDragHover(info);
    };

    const onDragEnter = (info) => {
        handleFolderDragHover(info);
    };

    const onDragLeave = (info) => {
        const areaKey = resolveAreaKeyFromEventNode(info?.node);
        if (areaKey && areaKey === pendingExpandKeyRef.current) {
            clearExpandTimer();
        }
    };

    const onDragEnd = () => {
        setIsDragging(false);
        clearExpandTimer();
        clearDragCollapseState();
    };

    const onDrop = async (info) => {
        const afterKeysSnapshot = [...dropAfterKeys];
        setIsDragging(false);
        clearExpandTimer();
        clearDragCollapseState();

        try {
            if (!info?.dragNode || !info?.node) return;

            if (info.dragNode.key === info.node.key) {
                return;
            }

            const forcedAfterFolderKey = resolveDropAfterFolderKey(
                info,
                afterKeysSnapshot,
                treeData
            );

            let dropInfo = info;
            let droppedTree;

            if (forcedAfterFolderKey) {
                droppedTree = moveNodeAfterSibling(
                    treeData,
                    info.dragNode.key,
                    forcedAfterFolderKey
                );
                dropInfo = {
                    ...info,
                    node: findNodeContext(treeData, forcedAfterFolderKey)?.node ?? info.node,
                    dropToGap: true,
                };
            } else {
                dropInfo = normalizeDropInfo(treeData, info);
                droppedTree = applyDrop(treeData, dropInfo);
            }

            syncRawOrdinamentoFromTree(droppedTree);
            setDisplayTreeData(droppedTree);

            const { aree, contenuti } = flattenForBackend(droppedTree);

            const contenutoSenzaArea = contenuti.find((c) => !c.id_area || c.id_area <= 0);
            if (contenutoSenzaArea) {
                setDisplayTreeData(null);
                alertService.warn(
                    'Un contenuto non può stare al di fuori di un\'area.',
                    { autoClose: true }
                );
                return;
            }

            const shouldExpandTargetArea =
                dropInfo.node?.nodeType === 'area'
                && !forcedAfterFolderKey
                && !dropInfo.dropToGap;

            if (shouldExpandTargetArea) {
                const targetAreaKey = dropInfo.node.key;
                setExpandedKeys((prev) => (
                    prev.includes(targetAreaKey) ? prev : [...prev, targetAreaKey]
                ));
            }

            if (typeof onReorder === 'function') {
                const saved = await onReorder({ id_corso: idCorso, aree, contenuti });
                if (saved === false) {
                    setDisplayTreeData(null);
                }
            }
        } catch (err) {
            setDisplayTreeData(null);
            console.error('Errore durante onDrop tree:', err);
            alertService.warn('Errore durante il riordino. Riprovare.', { autoClose: true });
        }
    };

    if (!treeData?.length) {
        return null;
    }

    return (
        <div
            className={`custom-tree-container${
                isDragging ? ' custom-tree-container--dragging' : ''
            }`}
        >
            <Tree
                treeData={treeData}
                titleRender={renderTreeTitle}
                draggable={{ icon: false, nodeDraggable: () => true }}
                allowDrop={allowDrop}
                onDragStart={onDragStart}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDragEnd={onDragEnd}
                onDrop={onDrop}
                onSelect={onSelect}
                onExpand={onExpand}
                expandedKeys={effectiveExpandedKeys}
                selectedKeys={selectedKeys}
                selectable
                showIcon={false}
                showLine={false}
                blockNode
                motion={null}
            />
        </div>
    );
};

export default CustomTree;
